import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useStore } from '#imports';

interface UseServiceAccountOptionsOptions {
	/** Limit the accounts to members of this workspace; resolves to no accounts while unset. */
	workspaceId?: MaybeRefOrGetter<string | null | undefined>;
}

/**
 * Loads service accounts and exposes them as `{ label, id }` select options,
 * optionally scoped to the members of a workspace.
 */
export async function useServiceAccountOptions(options: UseServiceAccountOptionsOptions = {}) {
	const { workspaceId } = options;
	const store = useStore();

	const { data: allServiceAccounts, refresh } = await store.MonospaceServiceAccount.query((q) => q.many({
		include: { workspaces: true },
	}));

	const serviceAccounts = computed(() => {
		if (workspaceId === undefined) return allServiceAccounts.value;
		const id = toValue(workspaceId);
		if (!id) return [];
		return allServiceAccounts.value.filter((account) => account.workspaces?.some((member) => member.workspaceId === id));
	});

	const serviceAccountOptions = computed(() => serviceAccounts.value.map((account) => ({
		label: account.fullName ?? account.id,
		id: account.id,
	})));

	return { serviceAccounts, serviceAccountOptions, refresh };
}
